import { Injectable } from '@angular/core'
import { first } from 'rxjs/operators'
import { ApiService } from './api.service'
import { GlobalService, replaceStringTags } from './global.service'
import { use } from './utils'

@Injectable({
  providedIn: 'root'
})
export class SmsService {

  constructor(private g: GlobalService) {
    console.info(`## ${this.constructor.name}`)
  }

  /**
   * Sends a single SMS
   * @param user The user object that will receive the SMS
   * @param message The message to be sent
   *
   * The message may contain tags to be replaced at the time of sending:
   * {{NAME}}, {{FIRST_NAME}}, {{PROGRAM}}, {{SEGMENT}}, {{EMAIL}}, {{POINTS}}
   *
   * Migrated from the legacy `messaging/sendSms` (BulkSMS) to the new
   * `messaging/sendSms` endpoint (Brevo). The new contract requires
   * `{ number, message, nick, sender }` and returns an `ApiResult`.
   */
  sendOne(user: any, message: string): Promise<any> {
    const api = use<ApiService>(ApiService)
    return api.post('messaging/sendSms', this.buildBody(user, message)).pipe(first()).toPromise()
  }

  /**
   * Sends multiple SMSs.
   *
   * The new API has no batch SMS endpoint, so this loops over the recipients
   * and calls `messaging/sendSms` for each one.
   */
  sendMany(users: any[], message: string): Promise<any> {
    const api = use<ApiService>(ApiService)
    return Promise.all(users.map(u => api.post('messaging/sendSms', this.buildBody(u, message)).pipe(first()).toPromise()))
  }

  private buildBody(user: any, message: string): any {
    return {
      number: this.formatNumber(user.mobile),
      message: replaceStringTags(user, message, this.g),
      nick: this.g.nick,
      sender: this.sender(),
    }
  }

  /** Brevo SMS sender: alphanumeric, max 11 characters. */
  private sender(): string {
    const raw = (this.g.program && (this.g.program.smsSender || this.g.program.name)) || 'yes'
    return String(raw).replace(/[^a-zA-Z0-9]/g, '').slice(0, 11) || 'yes'
  }

  /**
   * Formats a mobile phone number to include +351 and remove non-numeric characters
   * @param mobile The mobile phone number
   * @private
   */
  private formatNumber(mobile: string | number) {
    return '+351' + `${mobile}`.replace(/\D/g, '')
  }
}
